import React from 'react';
import { observer } from 'mobx-react-lite';
import { confirmAlert } from 'react-confirm-alert';
import { useTranslation } from 'react-i18next';
import { useRootStore } from '../../../../mobx/bridge';
import styles from './LoginScreen.module.css';
import commonStyles from '../../../../assets/stylesheets/common.module.css';
import connectSMSLogo from '../../../../assets/images/connect-chat-logo.svg';
import LocationPicker from './LocationPicker';
import socketIo from '../../../../socket/socketIo';
import CustomConfirmAlert from '../CustomConfirmAlert/CustomConfirmAlert';
import * as utils from '../../../../helpers/utils';

// eslint-disable-next-line prefer-arrow-callback,sonarjs/cognitive-complexity
const LoginScreen = observer(function LoginScreen() {
  const { commonStore, sendingEvents } = useRootStore();
  const { t } = useTranslation();

  const [loginTimeout, setLoginTimeout] = React.useState(null);

  React.useEffect(() => () => {
    if (loginTimeout) {
      clearTimeout(loginTimeout);
    }
  });

  function credentialsSubmitHandler(event) {
    event.preventDefault();

    socketIo.loginSocket.connect();
    commonStore.setAuthenticationLoading(true);
    sendingEvents.socketEmitClientLogin(commonStore.username, commonStore.password, null);

    setLoginTimeout(
      setTimeout(() => {
        if (commonStore.authenticationLoading) {
          confirmAlert({
            customUI: ({ onClose }) => (
              <CustomConfirmAlert
                title={t('LoginScreen.AuthenticationFailed')}
                falseText={t('Common.Close')}
                falseEvent={onClose}
              />
            ),
          });

          commonStore.setPassword('');
          commonStore.setAuthenticationLoading(false);
        }
      }, 31000)
    );
  }

  function usernameChangeHandler(event) {
    commonStore.setUsername(event.target.value.toLowerCase());
  }

  function passwordChangeHandler(event) {
    commonStore.setPassword(event.target.value);
  }

  if (commonStore.authenticationLoading) {
    return (
      <div className={styles.loginContainer}>
        <div className={styles.loginForm}>
          {!commonStore.cpHeaderHeight && !utils.inIframe() ? (
            <button
              type="button"
              onClick={commonStore.setLogoutStatus}
              className={styles.loginScreenCloseBtn}
            />
          ) : null}

          <img src={connectSMSLogo} alt="logo" className={commonStyles.connectSMSLogo} />

          <div className={commonStyles.spinner}>
            <div className={commonStyles.bounce1} />

            <div className={commonStyles.bounce2} />

            <div className={commonStyles.bounce3} />
          </div>

          <h1 className={styles.loadingScreenMessage}>{t('LoginScreen.Authenticating')}</h1>
        </div>
      </div>
    );
  }

  if (commonStore.showLocationPicker) {
    return <LocationPicker />;
  }

  return (
    <div className={styles.loginContainer} data-testid="loginContainer">
      <form onSubmit={credentialsSubmitHandler} className={styles.loginForm} autoComplete="on">
        <img src={connectSMSLogo} alt="logo" className={commonStyles.connectSMSLogo} />

        <input
          type="text"
          onChange={usernameChangeHandler}
          placeholder={t('LoginScreen.EnterYourEmail')}
          autoComplete="username"
          required
          className={styles.textField}
          value={commonStore.username == null ? '' : commonStore.username}
          maxLength={100}
          data-testid="usernameInput"
        />

        <input
          type="password"
          onChange={passwordChangeHandler}
          placeholder={t('LoginScreen.EnterYourPassword')}
          autoComplete="current-password"
          required
          className={styles.textField}
          value={commonStore.password == null ? '' : commonStore.password}
          maxLength={100}
          data-testid="passwordInput"
        />

        <input
          type="submit"
          value={t('Common.Connect')}
          className={styles.loginSubmitBtn}
          data-testid="submitBtn"
        />
      </form>
    </div>
  );
});
LoginScreen.whyDidYouRender = true;
LoginScreen.displayName = 'LoginScreen';

export default LoginScreen;
